'use strict';

(() => {
  window.mx = window.mx || {};

  window.mx.components.MxHeader = class extends window.mx.components.MxComponent {
    constructor() {
      super();

      this.TABLET_BREAKPOINT = 640;
      this.DESKTOP_BREAKPOINT = 1280;

      this.HIDDEN_SCROLL_THRESHOLD = 500;

      this.STICKY_MOBILE_SCROLL_THRESHOLD = 25;
      this.STICKY_DESKTOP_SCROLL_THRESHOLD = 35;

      this.lasScrollPosition = 0;
    }

    connectedCallback() {
      this.onScroll();
      this.events.add(window, 'scroll', window.mx.utils.throttle(this.onScroll), { passive: true });

      this.observers.resize(this, this.onResize);

      if (this.hamburgerContainer) {
        this.events.add(this.hamburgerContainer, 'click', this.onHamburgerClick);
      }

      if (this.cartUtility) {
        this.events.add(this.cartUtility, 'click', this.onCartUtilityClick);
      }

      if (this.chatLinks) {
        this.chatLinks.forEach((chatLink) => {
          this.events.add(chatLink, 'click', this.onChatLinkClick);
        });
      }

      this.mobileNavigationLinks.forEach((link) => {
        this.events.add(link, 'click', this.onMobileNavigationLinkClick);
      });

      this.subMenuHeaders.forEach((button) => {
        this.events.add(button, 'click', this.onSubMenuHeaderClick);
      });

      this.desktopNavigationLinkContainers.forEach((linkContainer) => {
        this.events.add(linkContainer, 'mouseenter', this.onDesktopNavigationMouseEnter);
        this.events.add(linkContainer, 'mouseleave', this.onDesktopNavigationMouseLeave);
      });
    }

    get hamburgerContainer() {
      return this.cache.get('dom', 'hamburgerContainer', () => {
        return this.querySelector('.hamburger-container');
      });
    }

    get mobileNavigationContainer() {
      return this.cache.get('dom', 'mobileNavigationContainer', () => {
        return this.querySelector('.mobile-navigation-container');
      });
    }

    get cartUtility() {
      return this.cache.get('dom', 'cartUtility', () => {
        return this.querySelector('.cart-utility');
      });
    }

    get chatLinks() {
      return this.cache.get('dom', 'chatLinks', () => {
        return Array.from(this.querySelectorAll('.chat-link'));
      });
    }

    get mobileNavigationLinks() {
      return this.cache.get('dom', 'mobileNavigationLinks', () => {
        return Array.from(this.querySelectorAll('.mobile-navigation-container .navigation-link'));
      });
    }

    get subMenuHeaders() {
      return this.cache.get('dom', 'subMenuHeaders', () => {
        return Array.from(this.querySelectorAll('.mobile-navigation-container .sub-menu-header'));
      });
    }

    get desktopNavigationLinkContainers() {
      return this.cache.get('dom', 'desktopNavigationLinkContainers', () => {
        return Array.from(
          this.querySelectorAll(
            '.desktop-navigation-container .navigation-link-container[aria-expanded], .search-utility[aria-expanded]',
          ),
        );
      });
    }

    /**
     * Determines if the user is scrolling down based on the last scroll position.
     * @returns {boolean} True if scrolling down, false otherwise.
     */
    get isScrollingDown() {
      const scrollingDown = window.scrollY > this.lastScrollPosition;
      this.lastScrollPosition = window.scrollY;
      return scrollingDown;
    }

    /**
     * Returns the scroll threshold for sticky behavior based on the screen width.
     * @returns {number} The scroll threshold for sticky behavior.
     */
    get stickyThreshold() {
      return this.cache.get('layout', 'stickyThreshold', () => {
        return window.screen.width > this.DESKTOP_BREAKPOINT
          ? this.STICKY_DESKTOP_SCROLL_THRESHOLD
          : this.STICKY_MOBILE_SCROLL_THRESHOLD;
      });
    }

    /**
     * Returns the overhead property value of the header.
     * @returns {string} The overhead property value of the header.
     */
    get headerOverhead() {
      return this.section.style.getPropertyValue('--header-overhead');
    }

    /**
     * Sets the overhead property value of the header.
     * @param {string} value The overhead property value to set.
     */
    set headerOverhead(value) {
      value === '0px'
        ? this.section.style.removeProperty('--header-overhead')
        : this.section.style.setProperty('--header-overhead', value);
    }

    /**
     * Returns the overhead property value of the mobile navigation.
     * @returns {string} The overhead property value of the mobile navigation.
     */
    get mobileNavigationOverhead() {
      return this.section.style.getPropertyValue('--mobile-navigation-overhead');
    }

    /**
     * Sets the overhead property value of the mobile navigation.
     * @param {string} value The overhead property value to set.
     */
    set mobileNavigationOverhead(value) {
      value === '0px'
        ? this.section.style.removeProperty('--mobile-navigation-overhead')
        : this.section.style.setProperty('--mobile-navigation-overhead', value);
    }

    /**
     * Returns whether the header is sticky.
     * @returns {boolean} True if the header is sticky, false otherwise.
     */
    get isSticky() {
      return this.modifiers.has('sticky');
    }

    /**
     * Sets whether the header is sticky.
     * @param {boolean} value True if the header should be sticky, false otherwise.
     */
    set isSticky(value) {
      this.modifiers.set('sticky', value);
    }

    /**
     * Returns whether the header is hidden.
     * @returns {boolean} True if the header is hidden, false otherwise.
     */
    get isHidden() {
      return this.modifiers.has('hidden');
    }

    /**
     * Sets whether the header is hidden.
     * @param {boolean} value True if the header should be hidden, false otherwise.
     */
    set isHidden(value) {
      this.modifiers.set('hidden', value);
    }

    /**
     * Returns whether the mobile menu is open.
     * @returns {boolean} True if the mobile menu is open, false otherwise.
     */
    get mobileMenuOpen() {
      return this.modifiers.has('mobile-menu-open');
    }

    /**
     * Sets whether the mobile menu is open.
     * @param {boolean} value True if the mobile menu should be open, false otherwise.
     */
    set mobileMenuOpen(value) {
      this.modifiers.set('mobile-menu-open', value);
    }

    onScroll = () => {
      this.headerOverhead = `${this.section.getBoundingClientRect().top}px`;

      if (this.modifiers.has('mobile-menu-open')) {
        this.mobileNavigationOverhead = `${this.mobileNavigationContainer.getBoundingClientRect().top}px`;
      } else {
        this.mobileNavigationOverhead = '0px';
      }

      if (window.mx.utils.scroll.isLocked) return;

      this.isSticky = window.scrollY > this.stickyThreshold;

      if (window.scrollY > this.HIDDEN_SCROLL_THRESHOLD && !this.modifiers.has('desktop-menu-open')) {
        this.isHidden = this.isScrollingDown;
      } else {
        this.isHidden = false;
      }
    };

    onResize = () => {
      this.cache.clear(['layout', 'style']);

      this.onScroll();

      if (this.mobileMenuOpen === true && window.screen.width > this.DESKTOP_BREAKPOINT) {
        this.onHamburgerClick();
      }
    };

    onHamburgerClick = () => {
      if (this.mobileMenuOpen) {
        this.mobileNavigationOverhead = '0px';

        this.mobileMenuOpen = false;
        this.querySelectorAll('.mobile-navigation-container [aria-expanded="true"]').forEach((el) => {
          el.setAttribute('aria-expanded', 'false');
        });

        window.mx.utils.scroll.unlock();

        // Dispatch a custom event to notify that the mobile menu has been closed
        this.dispatchEvent(
          new CustomEvent('mx-drawer:close', {
            bubbles: true,
            detail: { drawer: this },
          }),
        );
      } else {
        this.mobileNavigationOverhead = `${this.mobileNavigationContainer.getBoundingClientRect().top}px`;

        this.mobileMenuOpen = true;
        window.mx.utils.scroll.lock();

        // Dispatch a custom event to notify that the mobile menu has been opened
        this.dispatchEvent(
          new CustomEvent('mx-drawer:open', {
            bubbles: true,
            detail: { drawer: this },
          }),
        );
      }
    };

    onCartUtilityClick = () => {
      if (!this.cartDrawer) return;

      this.mobileMenuOpen = false;
      this.querySelectorAll('.mobile-navigation-container [aria-expanded="true"]').forEach((el) => {
        el.setAttribute('aria-expanded', 'false');
      });

      this.cartDrawer.isOpen = true;
    };

    onChatLinkClick = () => {
      this.mobileMenuOpen = false;
      this.querySelectorAll('.mobile-navigation-container [aria-expanded="true"]').forEach((el) => {
        el.setAttribute('aria-expanded', 'false');
      });

      window.mx.utils.gorgias.open();
    };

    onMobileNavigationLinkClick = (event) => {
      const linkContainer = event.target.closest('.navigation-link-container');

      if (!linkContainer.hasAttribute('aria-expanded')) return;

      event.preventDefault();

      const isExpanded = linkContainer.getAttribute('aria-expanded') === 'true';
      linkContainer.setAttribute('aria-expanded', !isExpanded);
    };

    onSubMenuHeaderClick = (event) => {
      const linkContainer = event.target.closest('.navigation-link-container');
      linkContainer.setAttribute('aria-expanded', 'false');
    };

    onDesktopNavigationMouseEnter = (event) => {
      this.modifiers.add('desktop-menu-open');
      event.currentTarget.setAttribute('aria-expanded', 'true');
    };

    onDesktopNavigationMouseLeave = (event) => {
      this.modifiers.remove('desktop-menu-open');
      event.currentTarget.setAttribute('aria-expanded', 'false');
    };
  };
  if (!window.customElements.get('mx-header')) {
    window.customElements.define('mx-header', window.mx.components.MxHeader);
  }
})();
